import { Box } from "@mui/system";
import { useMemo } from "react";
import { useDispatch } from "react-redux";

import { useAppSelector } from "~/app/store";
import {
  addEvent,
  selectActivePtlPhaseDetails,
  setPtlDeviceTestStatus
} from "~/features/testingTools/testingTools.slice";

import { usePTLSubscription } from "~/lib/signalr";

import {
  PTLEventDto,
  TestConfirmedDto,
  TotePlacementSummaryDto,
  WorkstationPortSummaryDto,
  WorkstationSummaryDto
} from "~/types/api";

import WorkstationGridCell from "./WorkstationGridCell";

type CellData = WorkstationPortSummaryDto &
  TotePlacementSummaryDto & {
    type: "port" | "tote";
    cellPosition: { x: number; y: number };
  };

const WorkstationCellGrid = ({
  selectedWorkstation
}: {
  selectedWorkstation: WorkstationSummaryDto;
}) => {
  const dispatch = useDispatch();
  const activePTLTestPhase = useAppSelector(selectActivePtlPhaseDetails);
  const shouldListenForPTLEvents =
    activePTLTestPhase.phaseIndex === "2" &&
    activePTLTestPhase.phase === "ping_verification" &&
    !!activePTLTestPhase?.deviceStatusById;

  const {
    wsCellDataMap,
    gridBounds: { minX, maxX, minY, maxY }
  } = useMemo(() => {
    const allWsCells = [
      ...selectedWorkstation.ports.map((port) => ({
        ...port,
        type: "port",
        cellPosition: port.coordinate
      })),
      ...selectedWorkstation.totePlacements.map((totePlacement) => ({
        ...totePlacement,
        type: "tote",
        cellPosition: totePlacement.coordinate
      }))
    ] as Array<CellData>;

    const xPositions = allWsCells.map((i) => i.cellPosition.x);
    const yPositions = allWsCells.map((i) => i.cellPosition.y);

    return {
      gridBounds: {
        minX: Math.min(...xPositions),
        maxX: Math.max(...xPositions),
        minY: Math.min(...yPositions),
        maxY: Math.max(...yPositions)
      },
      wsCellDataMap: new Map(
        allWsCells.map((cell) => [
          `${cell.cellPosition.x},${cell.cellPosition.y}`,
          cell
        ])
      )
    };
  }, [selectedWorkstation.ports, selectedWorkstation.totePlacements]);

  const gridWidth = maxX - minX + 1;
  const gridHeight = maxY - minY + 1;

  const gridIndices = useMemo(
    () => Array.from({ length: gridWidth * gridHeight }, (_, index) => index),
    [gridWidth, gridHeight]
  );

  const handlePtlEvent = (event: PTLEventDto) => {
    if (!shouldListenForPTLEvents) return;
    if (event.eventType === "TestConfirmed") {
      const { pickToLightId, autostoreGridId, workstationId } =
        event.data as TestConfirmedDto;
      if (
        autostoreGridId === selectedWorkstation.autostoreGridId &&
        workstationId === selectedWorkstation.id
      ) {
        dispatch(
          addEvent({
            text: `PTL test: ping received for ${pickToLightId}`,
            state: "Success",
            testOrigin: "PTL"
          })
        );
        dispatch(
          setPtlDeviceTestStatus({
            ptlId: pickToLightId,
            status: {
              pingOutcome: "success"
            }
          })
        );
      }
    }
  };

  usePTLSubscription(handlePtlEvent);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${gridWidth}, 200px)`,
        gridTemplateRows: `repeat(${gridHeight}, 200px)`,
        gap: 1,
        justifyItems: "center",
        alignSelf: "flex-start"
      }}
      aria-label="workstation cell grid"
      role="grid"
    >
      {gridIndices.map((index) => {
        const x = (index % gridWidth) + minX;
        const y = maxY - Math.floor(index / gridWidth);

        const cellData = wsCellDataMap.get(`${x},${y}`);
        const cellKeyAndId = `${!cellData?.type ? "empty" : cellData.type}-r${x}-c${y}`;

        return (
          <WorkstationGridCell
            key={cellKeyAndId}
            cellId={cellKeyAndId}
            cellData={cellData}
            selectedWorkstation={selectedWorkstation}
            cellPosition={{ x, y }}
          />
        );
      })}
    </Box>
  );
};
export default WorkstationCellGrid;
